import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { DEFAULT_MARKETING_CONFIG, type MarketingConfig } from "@/lib/site-marketing";
import { readSharedEdgeCache, writeSharedEdgeCache } from "@/server/site/edge-cache";

// Cache en memoria a nivel de módulo (mismo patrón que getSiteImageSlots en
// site-image-slots.fns.ts) — ver contact.fns.ts para la nota completa sobre
// el nivel L2 de Cache API (edge-cache.ts) que se suma a este L1 en memoria:
// comparte caché entre isolates del mismo datacenter, así que un isolate
// reciclado (frecuente bajo baja carga) ya no paga el round-trip completo a
// Supabase — el mutation que edita estas columnas, crmUpdateConfiguracion,
// corre en el Worker admin y no puede invalidar el nivel L1 (memoria) de
// este Worker público tras el split, así que sigue dependiendo solo del TTL.
let marketingCache: { data: MarketingConfig; expiresAt: number } | null = null;
const MARKETING_CACHE_TTL = 60_000;
// Ojo: este key también se referencia (como literal, no import — ver
// project-import-protection-fns) en configuracion.fns.ts para invalidar el
// nivel L2 (Cache API) cuando crmUpdateConfiguracion cambia estas columnas.
const MARKETING_EDGE_CACHE_KEY = "public-marketing-config";

/**
 * Lectura pública (sin autenticación) de la configuración de marketing del sitio.
 * Se usa en el loader de la ruta raíz para SSR. Degrada a DEFAULT_MARKETING_CONFIG
 * ante cualquier error: el sitio público nunca debe romperse por esta configuración.
 */
export const getPublicMarketingConfig = createServerFn({ method: "GET" }).handler(
  async (): Promise<MarketingConfig> => {
    if (marketingCache && Date.now() < marketingCache.expiresAt) return marketingCache.data;

    const fromEdge = await readSharedEdgeCache<MarketingConfig>(MARKETING_EDGE_CACHE_KEY);
    if (fromEdge) {
      marketingCache = { data: fromEdge, expiresAt: Date.now() + MARKETING_CACHE_TTL };
      return fromEdge;
    }

    try {
      const { data, error } = await supabaseAdmin
        .from("crm_configuracion")
        .select(
          "google_ads_conversion_id, google_ads_conversion_label, google_ads_whatsapp_conversion_label, google_tag_gateway_habilitado",
        )
        .eq("id", 1)
        .single();
      if (error) throw error;
      const resolved: MarketingConfig = {
        googleAdsConversionId: data.google_ads_conversion_id?.trim() || null,
        googleAdsConversionLabel: data.google_ads_conversion_label?.trim() || null,
        googleAdsWhatsappConversionLabel: data.google_ads_whatsapp_conversion_label?.trim() || null,
        googleTagGatewayEnabled: data.google_tag_gateway_habilitado ?? true,
      };
      marketingCache = { data: resolved, expiresAt: Date.now() + MARKETING_CACHE_TTL };
      await writeSharedEdgeCache(MARKETING_EDGE_CACHE_KEY, resolved, MARKETING_CACHE_TTL / 1000);
      return resolved;
    } catch (err) {
      console.error("[marketing] getPublicMarketingConfig fallback to defaults:", err);
      return DEFAULT_MARKETING_CONFIG;
    }
  },
);
